import { useState, useEffect } from 'react';
import { X, Loader2, CheckCircle, AlertCircle, Mail, ChevronRight, ExternalLink, Shield } from 'lucide-react';
import { addExternalAccount, syncExternalAccount } from '../../services/emailApi';

const IMAGIA_BASE = import.meta.env.VITE_IMAGIA_URL || 'https://imagia.net';

// Providers that use OAuth through Imagia (no password needed)
const OAUTH_PROVIDERS = [
  { key: 'gmail', name: 'Gmail', domain: '@gmail.com', icon: 'https://www.google.com/gmail/about/static-2.0/images/logo-gmail.png', oauth: true, scope: 'gmail' },
];

// Providers that use IMAP with app passwords
const IMAP_PROVIDERS = [
  { key: 'outlook', name: 'Outlook / Hotmail', domain: '@outlook.com', color: '#0078d4', letter: 'O', imap: 'outlook.office365.com', port: 993, passwordLabel: 'Password', passwordPlaceholder: 'Password' },
  { key: 'yahoo', name: 'Yahoo Mail', domain: '@yahoo.com', color: '#6001d2', letter: 'Y', imap: 'imap.mail.yahoo.com', port: 993, passwordLabel: 'App Password', passwordPlaceholder: 'App password', helpUrl: 'https://login.yahoo.com/account/security/app-passwords', helpText: 'Generate an app password in Yahoo settings' },
  { key: 'icloud', name: 'iCloud Mail', domain: '@icloud.com', color: '#333', letter: '', imap: 'imap.mail.me.com', port: 993, passwordLabel: 'App-Specific Password', passwordPlaceholder: 'xxxx-xxxx-xxxx-xxxx', helpUrl: 'https://appleid.apple.com/account/manage', helpText: 'Generate at appleid.apple.com → App-Specific Passwords' },
  { key: 'zoho', name: 'Zoho Mail', domain: '@zoho.com', color: '#d4382c', letter: 'Z', imap: 'imap.zoho.com', port: 993, passwordLabel: 'App Password', passwordPlaceholder: 'App password' },
  { key: 'custom', name: 'Other / Custom IMAP', domain: '', color: '#6b7280', letter: '⚙', imap: '', port: 993, passwordLabel: 'Password', passwordPlaceholder: 'Password' },
];

const ALL_PROVIDERS = [...OAUTH_PROVIDERS, ...IMAP_PROVIDERS];

export default function ExternalAccountModal({ isOpen, onClose, onAccountAdded, emailAddressId }) {
  const [step, setStep] = useState('pick'); // 'pick' | 'credentials' | 'oauth-pending'
  const [provider, setProvider] = useState(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [imapHost, setImapHost] = useState('');
  const [imapPort, setImapPort] = useState('993');
  const [label, setLabel] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // Listen for OAuth callback via URL params
  useEffect(() => {
    if (!isOpen) return;
    const params = new URLSearchParams(window.location.search);
    const authResult = params.get('google_auth');
    if (authResult === 'success') {
      const googleEmail = params.get('google_email');
      setSuccess(true);
      setEmail(googleEmail || '');
      // Clean up URL params
      const url = new URL(window.location.href);
      url.searchParams.delete('google_auth');
      url.searchParams.delete('google_email');
      window.history.replaceState({}, '', url.toString());
      // Notify parent
      setTimeout(() => {
        onAccountAdded({ external_email: googleEmail, account_type: 'gmail_api' });
        handleClose();
      }, 2000);
    } else if (authResult === 'error') {
      setError(params.get('error_message') || 'Google authentication failed');
      setStep('pick');
      const url = new URL(window.location.href);
      url.searchParams.delete('google_auth');
      url.searchParams.delete('error_message');
      window.history.replaceState({}, '', url.toString());
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const isCustom = provider?.key === 'custom';
  const isOAuth = provider?.oauth;

  function selectProvider(p) {
    if (p.oauth) {
      // OAuth flow — redirect to Imagia
      setProvider(p);
      setStep('oauth-pending');
      const currentUrl = window.location.href.split('?')[0];
      const authUrl = `${IMAGIA_BASE}/api/google/auth?scope=${p.scope}&redirect=${encodeURIComponent(currentUrl)}&source_app=jubly${emailAddressId ? `&email_address_id=${emailAddressId}` : ''}`;
      window.location.href = authUrl;
      return;
    }
    setProvider(p);
    setImapHost(p.imap);
    setImapPort(String(p.port));
    setStep('credentials');
    setError('');
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!email || !password) {
      setError('Email and password are required');
      return;
    }
    if (isCustom && !imapHost) {
      setError('IMAP Host is required for custom accounts');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const result = await addExternalAccount({
        email,
        password,
        imap_host: imapHost || undefined,
        imap_port: parseInt(imapPort) || 993,
        label: label || email,
      });

      setSuccess(true);

      if (result.account?.id) {
        syncExternalAccount(result.account.id).catch(() => {});
      }

      setTimeout(() => {
        onAccountAdded(result.account);
        handleClose();
      }, 1500);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to connect account');
    } finally {
      setLoading(false);
    }
  }

  function handleClose() {
    setStep('pick');
    setProvider(null);
    setEmail('');
    setPassword('');
    setImapHost('');
    setImapPort('993');
    setLabel('');
    setError('');
    setSuccess(false);
    onClose();
  }

  function handleBack() {
    setStep('pick');
    setProvider(null);
    setEmail('');
    setPassword('');
    setError('');
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={handleClose}>
      <div
        className="bg-white dark:bg-gray-900 rounded-xl shadow-xl w-full max-w-md mx-4 p-6"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            {step === 'credentials' && (
              <button onClick={handleBack} className="p-1 hover:bg-gray-100 rounded -ml-1 mr-1">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" /></svg>
              </button>
            )}
            <Mail size={20} />
            {step === 'pick' ? 'Add Email Account' : step === 'oauth-pending' ? 'Connecting...' : `Connect ${provider?.name}`}
          </h2>
          <button onClick={handleClose} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded">
            <X size={18} />
          </button>
        </div>

        {success ? (
          <div className="flex flex-col items-center gap-3 py-8 text-green-600">
            <CheckCircle size={48} />
            <p className="font-medium">Account connected!</p>
            {email && <p className="text-sm text-gray-500">{email}</p>}
            <p className="text-sm text-gray-400">Syncing emails in the background...</p>
          </div>
        ) : step === 'oauth-pending' ? (
          /* Waiting for OAuth redirect */
          <div className="flex flex-col items-center gap-4 py-8">
            <Loader2 size={32} className="animate-spin text-blue-600" />
            <p className="text-sm text-gray-600">Redirecting to Google...</p>
            <p className="text-xs text-gray-400">You'll be asked to sign in and grant access</p>
          </div>
        ) : step === 'pick' ? (
          /* Provider picker */
          <div className="space-y-1.5">
            <p className="text-sm text-gray-500 mb-3">Choose your email provider</p>
            {ALL_PROVIDERS.map(p => (
              <button
                key={p.key}
                onClick={() => selectProvider(p)}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors text-left group"
              >
                {p.icon ? (
                  <img src={p.icon} alt="" className="w-6 h-6 rounded" />
                ) : (
                  <div className="w-6 h-6 rounded flex items-center justify-center text-white text-xs font-bold" style={{ backgroundColor: p.color }}>
                    {p.letter}
                  </div>
                )}
                <span className="flex-1 text-sm font-medium text-gray-800">{p.name}</span>
                {p.oauth && (
                  <span className="text-[10px] text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">OAuth</span>
                )}
                <ChevronRight size={16} className="text-gray-300 group-hover:text-gray-500" />
              </button>
            ))}

            {/* Powered by Imagia notice */}
            <div className="mt-4 pt-3 border-t border-gray-100 flex items-center gap-2 px-1">
              <Shield size={14} className="text-gray-400 flex-shrink-0" />
              <p className="text-[11px] text-gray-400">
                Google authentication is securely powered by{' '}
                <a href="https://imagia.net" target="_blank" rel="noopener noreferrer" className="text-indigo-500 hover:underline">
                  Imagia
                </a>
              </p>
            </div>
          </div>
        ) : (
          /* IMAP credentials form */
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Email Address</label>
              <input
                type="email"
                value={email}
                onChange={e => { setEmail(e.target.value); setError(''); }}
                placeholder={provider?.domain ? `you${provider.domain}` : 'you@example.com'}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-transparent focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                required
                autoFocus
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">{provider?.passwordLabel || 'Password'}</label>
              <input
                type="password"
                value={password}
                onChange={e => { setPassword(e.target.value); setError(''); }}
                placeholder={provider?.passwordPlaceholder || 'Password'}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-transparent focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                required
              />
              {provider?.helpUrl && (
                <p className="text-xs text-gray-500 mt-1">
                  {provider.helpText || 'An app-specific password is required.'}{' '}
                  <a href={provider.helpUrl} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">
                    Create one here
                  </a>
                </p>
              )}
            </div>

            {isCustom && (
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-2">
                  <label className="block text-sm font-medium mb-1">IMAP Host</label>
                  <input
                    type="text"
                    value={imapHost}
                    onChange={e => setImapHost(e.target.value)}
                    placeholder="imap.example.com"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-transparent focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Port</label>
                  <input
                    type="number"
                    value={imapPort}
                    onChange={e => setImapPort(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-transparent focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                  />
                </div>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium mb-1">Display Label (optional)</label>
              <input
                type="text"
                value={label}
                onChange={e => setLabel(e.target.value)}
                placeholder={email || provider?.name || 'My email'}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-transparent focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
              />
            </div>

            {error && (
              <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 dark:bg-red-900/20 px-3 py-2 rounded-lg">
                <AlertCircle size={16} />
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading || !email || !password}
              className="w-full py-2.5 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  Testing connection...
                </>
              ) : (
                'Connect Account'
              )}
            </button>
          </form>
        )}

        {/* Error from OAuth redirect */}
        {error && step === 'pick' && (
          <div className="mt-3 flex items-center gap-2 text-sm text-red-600 bg-red-50 dark:bg-red-900/20 px-3 py-2 rounded-lg">
            <AlertCircle size={16} />
            {error}
          </div>
        )}
      </div>
    </div>
  );
}
